// 실험실(유사기업·버핏) 진행 로그 파싱 — 백엔드가 보내는 진행 문자열은 전부
// "Step 2/6 · 사업보고서 2,431개에서 …" 꼴이다(app/valuation/peer/{peer,us_peer}.py
// 의 _emit_step). 화면은 이 규격을 표로 세운다.
export const STEP_RE = /^Step\s+(\d+)\s*\/\s*(\d+)\s*·\s*(.+)$/;
// 하트비트는 살아 있다는 신호일 뿐 단계가 아니다 — 표에서 빼고 '마지막 갱신'에만 쓴다
export const HEARTBEAT_RE = /^분석 계속 진행 중입니다/;

/** 유사기업 선정 6단계 기본 이름.
 *
 *  모집단 캐시가 맞으면 백엔드가 2~3단계를 아예 보내지 않는다(건너뛰므로).
 *  그러면 화면에 1 다음 4가 튀어나와 단계가 사라진 것처럼 보인다. 안 온 단계도
 *  이름을 갖고 자리를 지키도록 기본값을 둔다. 실제 문구가 오면 그것으로 덮인다.
 */
export const PEER_STEP_TITLES = [
  "핵심 키워드 추출 · 전 상장사 사업보고서 로드",
  "사업보고서 키워드 매칭",
  "매출구조 관련성 정밀 검토",
  "정성적 제외 기준 적용",
  "재무 데이터 · 거래정지 이력 조회",
  "종합 점수 산정 · 최종 선정",
];

export interface StepRow {
  no: number;
  text: string;
  /** done 은 실제로 돈 것과 건너뛴 것을 함께 뜻한다 — skipped 로 구분한다 */
  state: "done" | "now" | "wait";
  /** 캐시 재사용으로 백엔드가 통지하지 않은 단계 */
  skipped: boolean;
  secs: number | null;
}

/**
 * 진행 문자열 목록 → 단계 표. 같은 단계가 여러 번 오면 마지막 문구를 쓴다.
 *
 * @param titles 안 온 단계의 기본 이름. 길이가 곧 총 단계 수의 하한이라,
 *               아직 아무 문구도 안 왔을 때에도 표가 선다.
 */
export function buildStepRows(
  steps: string[],
  seenAt: Record<string, number>,
  running: boolean,
  now: number,
  titles: string[] = [],
): StepRow[] {
  const text = new Map<number, string>();
  const firstAt = new Map<number, number>();
  let total = titles.length, maxNo = 0;
  for (const s of steps) {
    const m = STEP_RE.exec(s);
    if (!m) continue;
    const no = Number(m[1]);
    total = Math.max(total, Number(m[2]));
    maxNo = Math.max(maxNo, no);
    text.set(no, m[3].trim());
    const at = seenAt[s];
    if (at && (!firstAt.has(no) || at < (firstAt.get(no) as number))) firstAt.set(no, at);
  }
  if (!total) return [];
  // 끝났으면 전 단계가 완료다 — 캐시로 건너뛴 단계까지 포함해서.
  const doneUpTo = running ? maxNo : total;
  const rows: StepRow[] = [];
  for (let no = 1; no <= total; no++) {
    const state: StepRow["state"] =
      no < doneUpTo ? "done" : no === doneUpTo ? (running ? "now" : "done") : "wait";
    // 지나갔는데 문구가 안 온 단계 = 캐시 재사용으로 건너뛴 것
    const skipped = state === "done" && !text.has(no);
    // 소요 = 이 단계가 처음 보인 때부터 다음 단계가 시작할 때까지.
    // 진행 중인 단계는 지금까지의 경과. 시각을 모르면(복원된 로그·건너뜀) null.
    const from = firstAt.get(no);
    const to = state === "now" ? now : firstAt.get(no + 1);
    rows.push({
      no,
      text: text.get(no) || titles[no - 1] || (state === "wait" ? "대기 중" : "진행 내용 미기록"),
      state,
      skipped,
      secs: from && to && to >= from ? Math.round((to - from) / 1000) : null,
    });
  }
  return rows;
}
